import {
  TransactionService,
  TransactionCustomization,
} from 'services/api/entities/accounting/transaction/types/internal/transaction-models'
import { CountryCode } from 'services/cfg/countries'
import { SupportedCountryCode } from 'services/geo-management'
import { getAggregateValue } from 'services/utils'
import {
  getDiscountedPrice,
  getGrossFromNet,
  getMargin,
  getMarginPerc,
  getVatEffect,
  getVatFromNet,
  GLOBAL_VAT_RATE,
} from 'services/utils/prices'
import { Memoizer } from 'types/common'

const getVatRateByBusinessCountry = (
  countryCode: SupportedCountryCode,
  businessCountry?: CountryCode
) => (businessCountry && businessCountry !== countryCode ? 0 : GLOBAL_VAT_RATE)

export type WithServiceName = {
  serviceName: string
}

export type CustomRecord = {
  price: number
  grossPrice: number
  inboundVat: number
  totalCost: number
  costNoVat: number
  costVatPerc: number
  costVat: number
  margin: number
  vatEffect: number
  marginPerc: number
  serviceName: string
  extraCost?: number
  extraPrice?: number
}

export type CustomRecordForAggregates = Omit<CustomRecord, 'costVatPerc'>

export type CustomAggregateRecord = Omit<
  CustomRecord,
  'costVatPerc' | 'serviceName' | 'extraPrice' | 'extraCost'
> & {
  discountedTotalMargin: number
  discountedTotalMarginPerc: number
  discount: number
  discountPerc: number
  grossPrice: number
  discountedPrice: number
  grossDiscountedPrice: number
  grossDiscount: number
  discountedPriceInboundVat: number
}

export const buildUseComputedRecord =
  (memoizer: Memoizer, countryCode: SupportedCountryCode) =>
  (
    partialRecord: TransactionService,
    customerBusinessCountry?: CountryCode
  ): CustomRecord => {
    const { costVatPerc, customization, ...rest } = partialRecord
    const extraPrice = customization?.price ?? 0
    const extraCost = customization?.cost ?? 0

    const price = partialRecord.price + extraPrice
    const costNoVat = partialRecord.costNoVat + extraCost
    // if the customer has a businessCountry associated and it's different that the country we are operating we apply 0 vat rate
    const vatRate = getVatRateByBusinessCountry(
      countryCode,
      customerBusinessCountry
    )

    const inboundVat = memoizer(
      () => getVatFromNet(price, vatRate),
      [price, vatRate]
    )

    const grossPrice = memoizer(() => price + inboundVat, [price, inboundVat])

    const costWithVat = memoizer(
      () => getGrossFromNet(costNoVat, costVatPerc),
      [costNoVat, costVatPerc]
    )

    const totalCost = costWithVat + (partialRecord.deliveryCost ?? 0)

    const costVat = memoizer(
      () => getVatFromNet(costNoVat, costVatPerc),
      [costNoVat, costVatPerc]
    )
    const vatEffect = memoizer(
      () => getVatEffect(costVat, inboundVat),
      [costVat, inboundVat]
    )
    const margin = memoizer(
      () => getMargin(price, costNoVat),
      [costVat, costNoVat]
    )
    const marginPerc = memoizer(
      () => getMarginPerc(margin, price),
      [margin, price]
    )

    return memoizer(
      () => ({
        ...rest,
        costVatPerc,
        grossPrice,
        price,
        inboundVat,
        totalCost,
        costNoVat,
        costVat,
        margin,
        vatEffect,
        marginPerc,
      }),
      [
        price,
        grossPrice,
        inboundVat,
        totalCost,
        costNoVat,
        costVat,
        margin,
        vatEffect,
        marginPerc,
      ]
    )
  }

export const buildUseAggregateComputedRecord =
  (memoizer: Memoizer, countryCode: SupportedCountryCode) =>
  (
    aggregateRecord: CustomRecordForAggregates[],
    customizations: TransactionCustomization[],
    discountPerc: number,
    customerBusinessCountry?: CountryCode
  ): CustomAggregateRecord => {
    // if the customer has a businessCountry associated and it's different that the country we are operating we apply 0 vat rate
    const vatRate = getVatRateByBusinessCountry(
      countryCode,
      customerBusinessCountry
    )

    const [customizationsCost, customizationsPrice] = customizations.reduce(
      ([prevCost, prevPrice], { cost, price }) => [
        prevCost + cost,
        prevPrice + price,
      ],
      [0, 0]
    )
    const customizationsCostVat = getVatFromNet(customizationsCost)
    const customizationsGrossCost = getGrossFromNet(customizationsCost)

    const price = memoizer(
      () =>
        getAggregateValue(aggregateRecord, (i) => i.price) +
        customizationsPrice,
      [aggregateRecord, customizationsPrice]
    )
    const inboundVat = memoizer(
      () => getVatFromNet(price, vatRate),
      [price, vatRate]
    )
    const grossPrice = memoizer(() => price + inboundVat, [price, inboundVat])

    const discountedPrice = memoizer(
      () => getDiscountedPrice(price, discountPerc),
      [discountPerc, price]
    )
    const discountedPriceInboundVat = memoizer(
      () => getVatFromNet(discountedPrice, vatRate),
      [discountedPrice]
    )
    const grossDiscountedPrice = memoizer(
      () => discountedPrice + discountedPriceInboundVat,
      [discountedPrice, discountedPriceInboundVat]
    )

    const discount = memoizer(
      () => price - discountedPrice,
      [price, discountedPrice]
    )
    const grossDiscount = memoizer(() => getGrossFromNet(discount), [discount])

    // cost vats are dynamic, so we do need to calculate the total costs by the sum of all the services totalCosts
    // and add to them the event customizations costs and vats separately, since these are vatted in a static way
    const totalCost = memoizer(
      () =>
        getAggregateValue(aggregateRecord, (i) => i.totalCost) +
        customizationsGrossCost,
      [aggregateRecord, customizationsGrossCost]
    )

    const costNoVat = memoizer(
      () =>
        getAggregateValue(aggregateRecord, (itm) => itm.costNoVat) +
        customizationsCost,
      [aggregateRecord, customizationsCost]
    )

    const costVat = memoizer(
      () =>
        getAggregateValue(aggregateRecord, (itm) => itm.costVat) +
        customizationsCostVat,
      [aggregateRecord, customizationsCostVat]
    )

    const vatEffect = memoizer(
      () => getVatEffect(costVat, discountedPriceInboundVat),
      [costVat, discountedPriceInboundVat]
    )
    const margin = memoizer(
      () => getMargin(price, costNoVat),
      [price, costNoVat]
    )
    const discountedTotalMargin = memoizer(
      () => getMargin(discountedPrice, costNoVat),
      [discountedPrice, costNoVat]
    )
    const marginPerc = memoizer(
      () => getMarginPerc(margin, price),
      [margin, price]
    )
    const discountedTotalMarginPerc = memoizer(
      () => getMarginPerc(discountedTotalMargin, discountedPrice),
      [discountedTotalMargin, discountedPrice]
    )

    return memoizer(
      () => ({
        grossPrice,
        price,
        grossDiscountedPrice,
        discountedPrice,
        discountPerc,
        grossDiscount,
        discount,
        inboundVat,
        discountedPriceInboundVat,
        totalCost,
        costNoVat,
        costVat,
        margin,
        discountedTotalMargin,
        marginPerc,
        discountedTotalMarginPerc,
        vatEffect,
      }),
      [
        price,
        grossPrice,
        discountedPrice,
        grossDiscountedPrice,
        discountPerc,
        discount,
        grossDiscount,
        inboundVat,
        discountedPriceInboundVat,
        totalCost,
        costNoVat,
        costVat,
        margin,
        discountedTotalMargin,
        marginPerc,
        discountedTotalMarginPerc,
        vatEffect,
      ]
    )
  }
